/**
 * Meta Activity Filter Service
 *
 * Filters and collapses intermediate status transitions from Meta activities
 * to show only meaningful state changes in the UI.
 *
 * Example transformation:
 * Input:  [Inactive→Pending(t1), Pending→PendingReview(t2), PendingReview→Active(t3)]
 * Output: [Inactive→Active(t3)]
 */

/**
 * Intermediate status values that should be filtered out
 * These are temporary states Meta uses during status transitions
 *
 * Different entity types have different intermediate statuses:
 * - Campaigns & AdSets: Only "Pending process"
 * - Ads: "Pending process", "Pending Review", "Pending"
 */
const INTERMEDIATE_STATUSES_CAMPAIGN = ['Pending process']
const INTERMEDIATE_STATUSES_ADSET = ['Pending process']
const INTERMEDIATE_STATUSES_AD = ['Pending process', 'Pending Review', 'Pending']

/**
 * Event types that represent status changes and should be collapsed
 *
 * Note: update_ad_run_status_to_be_set_after_review is intentionally EXCLUDED
 * This event type represents user INTENT (what they clicked to set after review)
 * but not the ACTUAL status. The actual ad status is tracked by update_ad_run_status.
 * Including both would create duplicate "turned on/off" actions in the UI.
 */
const STATUS_CHANGE_EVENT_TYPES = ['update_campaign_run_status', 'update_ad_set_run_status', 'update_ad_run_status']

/**
 * Determine entity type from event type
 * @param {string} eventType - Event type string
 * @returns {string} Entity type: 'campaign', 'adset', or 'ad'
 */
function getEntityType(eventType) {
    if (eventType.includes('campaign')) return 'campaign'
    if (eventType.includes('ad_set')) return 'adset'
    return 'ad'
}

/**
 * Get intermediate statuses for specific entity type
 * @param {string} eventType - Event type to determine entity
 * @returns {Array<string>} Array of intermediate statuses for this entity type
 */
function getIntermediateStatusesForEvent(eventType) {
    const entityType = getEntityType(eventType)
    switch (entityType) {
        case 'campaign':
            return INTERMEDIATE_STATUSES_CAMPAIGN
        case 'adset':
            return INTERMEDIATE_STATUSES_ADSET
        case 'ad':
            return INTERMEDIATE_STATUSES_AD
        default:
            return INTERMEDIATE_STATUSES_AD
    }
}

/**
 * Check if a status value is an intermediate state for the given event type
 * @param {string} status - Status value to check
 * @param {string} eventType - Event type to determine which intermediates to check
 * @returns {boolean} True if status is intermediate for this entity type
 */
function isIntermediateStatus(status, eventType) {
    const intermediates = getIntermediateStatusesForEvent(eventType)
    return intermediates.includes(status)
}

/**
 * Check if an activity is a status change event
 * @param {Object} activity - Activity object
 * @returns {boolean} True if activity represents a status change
 */
function isStatusChangeActivity(activity) {
    return STATUS_CHANGE_EVENT_TYPES.includes(activity.event_type)
}

/**
 * Extract old and new status values from activity extra_data
 * @param {string} extraData - JSON string containing activity metadata
 * @returns {{oldStatus: string|null, newStatus: string|null}} Parsed status values
 */
function extractStatusValues(extraData) {
    try {
        const parsed = extraData ? JSON.parse(extraData) : null
        return {
            oldStatus: parsed?.old_value || null,
            newStatus: parsed?.new_value || null,
        }
    } catch (error) {
        console.warn('Error parsing extra_data:', error)
        return { oldStatus: null, newStatus: null }
    }
}

/**
 * Collapse a sequence of status change activities by removing intermediate states
 *
 * Simple rule: Only show transitions that START from a real (non-intermediate) status
 * and walk forward to find where they END (the final non-intermediate status).
 *
 * Example:
 *   Input:  [Inactive→Pending(t1), Pending→PendingReview(t2), PendingReview→Active(t3)]
 *   Output: [Inactive→Active(t3)]
 *
 * Any transition that starts FROM an intermediate status is skipped (it's part of a chain we already processed).
 *
 * @param {Array} statusActivities - Array of status change activities for same object, sorted chronologically
 * @returns {Array} Collapsed activities with intermediates removed
 */
function collapseStatusSequence(statusActivities) {
    if (statusActivities.length === 0) return []

    // Build a simple collapse by tracking transitions
    const result = []
    let i = 0

    while (i < statusActivities.length) {
        const current = statusActivities[i]
        const { oldStatus, newStatus } = extractStatusValues(current.extra_data)

        const oldIsIntermediate = isIntermediateStatus(oldStatus, current.event_type)
        const newIsIntermediate = isIntermediateStatus(newStatus, current.event_type)

        // Skip transitions between two intermediate statuses
        if (oldIsIntermediate && newIsIntermediate) {
            i++
            continue
        }

        // Keep orphaned chain ends (FROM intermediate TO real status)
        // Example: "Pending Review → Active" even if we don't have the start
        if (oldIsIntermediate && !newIsIntermediate) {
            result.push(current)
            i++
            continue
        }

        // Keep direct transitions between real statuses
        if (!oldIsIntermediate && !newIsIntermediate) {
            result.push(current)
            i++
            continue
        }

        // For transitions TO intermediate, walk forward to find the end
        if (!oldIsIntermediate && newIsIntermediate) {
            let finalActivity = current
            let finalStatus = newStatus
            let j = i + 1

            // Walk forward until we find a non-intermediate final state
            while (j < statusActivities.length) {
                const next = statusActivities[j]
                const nextStatuses = extractStatusValues(next.extra_data)

                // Continue if this is part of our chain
                if (nextStatuses.oldStatus === finalStatus) {
                    finalActivity = next
                    finalStatus = nextStatuses.newStatus

                    // Stop when we reach a real status
                    if (!isIntermediateStatus(finalStatus, next.event_type)) {
                        break
                    }
                    j++
                } else {
                    // Chain broken
                    break
                }
            }

            // Only keep if we found a real end state
            if (!isIntermediateStatus(finalStatus, finalActivity.event_type)) {
                result.push({
                    ...finalActivity,
                    extra_data: JSON.stringify({
                        ...JSON.parse(finalActivity.extra_data),
                        old_value: oldStatus, // Keep the original start state
                        new_value: finalStatus, // Use the final end state
                    }),
                })
            }

            i = j + 1
            continue
        }

        i++
    }

    return result
}

/**
 * Filter and collapse intermediate status transitions from Meta activities
 *
 * This function:
 * 1. Groups activities by object_id and event type
 * 2. Identifies status change sequences with intermediate states
 * 3. Collapses sequences to show only meaningful transitions
 * 4. Preserves timestamp from the final transition (when state was achieved)
 * 5. Leaves non-status activities (budget, targeting, etc.) unchanged
 *
 * @param {Array} activities - Array of Meta activity objects
 * @returns {Array} Filtered activities with intermediate statuses collapsed
 *
 * @example
 * // Ad activation with multiple intermediate states
 * const activities = [
 *   { event_type: 'update_ad_run_status', object_id: '123', extra_data: '{"old_value":"Inactive","new_value":"Pending process"}', event_time: '2025-01-01T10:00:00Z' },
 *   { event_type: 'update_ad_run_status', object_id: '123', extra_data: '{"old_value":"Pending process","new_value":"Pending Review"}', event_time: '2025-01-01T10:01:00Z' },
 *   { event_type: 'update_ad_run_status', object_id: '123', extra_data: '{"old_value":"Pending Review","new_value":"Active"}', event_time: '2025-01-01T10:05:00Z' },
 *   { event_type: 'update_ad_set_budget', object_id: '123', extra_data: '{"old_value":100,"new_value":200}', event_time: '2025-01-01T11:00:00Z' }
 * ]
 *
 * const filtered = filterIntermediateStatuses(activities)
 * // Returns:
 * // [
 * //   { event_type: 'update_ad_run_status', object_id: '123', extra_data: '{"old_value":"Inactive","new_value":"Active"}', event_time: '2025-01-01T10:05:00Z' },
 * //   { event_type: 'update_ad_set_budget', object_id: '123', extra_data: '{"old_value":100,"new_value":200}', event_time: '2025-01-01T11:00:00Z' }
 * // ]
 */
export function filterIntermediateStatuses(activities) {
    if (!activities || activities.length === 0) {
        return []
    }

    // Separate status change activities from others
    // Skip update_ad_run_status_to_be_set_after_review entirely (user intent, not actual status)
    const statusActivities = []
    const nonStatusActivities = []

    for (const activity of activities) {
        // Skip update_ad_run_status_to_be_set_after_review events
        if (activity.event_type === 'update_ad_run_status_to_be_set_after_review') {
            continue
        }

        if (isStatusChangeActivity(activity)) {
            statusActivities.push(activity)
        } else {
            nonStatusActivities.push(activity)
        }
    }

    // Group status activities by object_id
    const activitiesByObject = {}
    for (const activity of statusActivities) {
        const objectId = activity.object_id
        if (!activitiesByObject[objectId]) {
            activitiesByObject[objectId] = []
        }
        activitiesByObject[objectId].push(activity)
    }

    // Collapse sequences for each object
    const collapsedStatusActivities = []
    for (const objectId in activitiesByObject) {
        const objectActivities = activitiesByObject[objectId]

        // Sort by event_time chronologically (oldest first)
        objectActivities.sort((a, b) => {
            const timeA = new Date(a.event_time).getTime()
            const timeB = new Date(b.event_time).getTime()
            return timeA - timeB
        })

        // Collapse the sequence
        const collapsed = collapseStatusSequence(objectActivities)
        collapsedStatusActivities.push(...collapsed)
    }

    // Combine collapsed status activities with non-status activities
    const allActivities = [...collapsedStatusActivities, ...nonStatusActivities]

    // Sort by event_time to maintain chronological order
    allActivities.sort((a, b) => {
        const timeA = new Date(a.event_time).getTime()
        const timeB = new Date(b.event_time).getTime()
        return timeB - timeA // Descending order (newest first)
    })

    return allActivities
}
